import type { RelativeDateRange } from 'components/datepicker/types';
import { TIMESPAN_DAY, TIMESPAN_MONTH, TIMESPAN_WEEK, TIMESPAN_YEAR } from 'constants/filters';
import type {
  ReportQuery,
  ReportQueryExtended,
  ReportSelection,
} from 'redux/modules/superreports.types';
import type { Query as UsersListPageQuery } from 'redux/modules/users-page/users-list/usersList.types';
import type { StartOfWeek } from 'types/account';
import { TagFilterMode } from 'types/tag';
import type { DeepPartial } from 'types/utility';
import {
  getDayRangeInMonth,
  getDayRangeInWeek,
  getDayRangeInYear,
  isSameDay,
  isSameMonth,
  isSameWeek,
  isSameYear,
} from 'utils/date-helper';
import { fromISO } from 'utils/luxon-helpers';

interface Options {
  defaultValues?: boolean;
  relativeDates?: boolean;
  weekStartsOn: StartOfWeek;
}

export const reportQueryFactory = (
  querySeed: DeepPartial<ReportQuery & { date_range: RelativeDateRange }> = {},
  { defaultValues = false, relativeDates = false, weekStartsOn }: Options
): ReportQueryExtended => {
  // Default values are optional.
  // We do this, because unset query/filters indicates not ready to query.
  const datenow = new Date(Date.now()).toISOString();

  const daysInMonth = getDayRangeInMonth(datenow, { weekStartsOn });
  const defaultFrom = defaultValues ? daysInMonth.from : undefined;
  const defaultTo = defaultValues ? daysInMonth.to : undefined;
  const defaultSpan = defaultValues ? TIMESPAN_MONTH : undefined;

  // report.query.from/to should no longer be used on reports
  // as we want them relative to "now"
  let from = querySeed.from ?? undefined;
  let to = querySeed.to ?? undefined;

  if (relativeDates && from && to) {
    // Month (re-use defaults from above)
    if (querySeed.span === TIMESPAN_MONTH && isSameMonth(from, to)) {
      from = daysInMonth.from;
      to = daysInMonth.to;
    }
    // Week
    if (querySeed.span === TIMESPAN_WEEK && isSameWeek(from, to, weekStartsOn)) {
      const daysInWeek = getDayRangeInWeek(datenow, weekStartsOn);
      from = daysInWeek.from;
      to = daysInWeek.to;
    }
    // Year
    if (querySeed.span === TIMESPAN_YEAR && isSameYear(from, to)) {
      const daysInYear = getDayRangeInYear(datenow);
      from = daysInYear.from;
      to = daysInYear.to;
    }

    if (querySeed.span === TIMESPAN_DAY && isSameDay(from, to)) {
      const today = fromISO(datenow).toISODate();
      from = today;
      to = today;
    }
  }

  return {
    from: from || defaultFrom,
    to: to || defaultTo,
    relativeDateRange: querySeed.date_range,
    span: querySeed.span || defaultSpan,
    filters: {
      project_ids: [],
      team_ids: [],
      user_ids: [],
      label_ids: [],
      label_filter: TagFilterMode.OR,
      state_ids: [],
      billed: null,
      locked: null,
      external_link_filter: undefined,
      sync_status_filter: undefined,
      ...querySeed.filters,
    },
    page: querySeed.page ?? 1,
  };
};

export const reportFilterSelectionFactory = (
  modifiers?: DeepPartial<ReportSelection>
): ReportSelection => ({
  hourIds: [],
  rangeEndHourId: null,
  rangeStartHourId: null,
  labelIds: [],
  billedState: null,
  lockedState: null,
  projectId: null,
  // stateId is intentionally omitted - 0 means "don't change state"
  ...modifiers,
});

export const usersPageQueryFilterSelection = (modifiers: UsersListPageQuerySeed['filters']) => ({
  userIds: [],
  teamIds: [],
  ...modifiers,
});

export const usersPageSort = (modifiers: UsersListPageQuerySeed['sort']) => ({
  by: 'name',
  direction: 'desc' as const,
  ...modifiers,
});

type UsersListPageQuerySeed = DeepPartial<UsersListPageQuery>;

export const usersPageFilterFactory = (
  seed: UsersListPageQuerySeed,
  weekStartsOn: StartOfWeek
): UsersListPageQuery => {
  const now = new Date().toISOString();
  const defaults: UsersListPageQuerySeed = {
    span: TIMESPAN_WEEK,
    show: 'active',
  };

  let from: string | undefined;
  let to: string | undefined;

  if (seed.span === TIMESPAN_MONTH) {
    const dateSpan = getDayRangeInMonth(now);
    from = dateSpan.from;
    to = dateSpan.to;
  }

  if (seed.span === TIMESPAN_WEEK) {
    const dateSpan = getDayRangeInWeek(now, weekStartsOn);
    from = dateSpan.from;
    to = dateSpan.to;
  }

  const filter: UsersListPageQuery = {
    ...defaults,
    ...seed,
    from: seed.from || from,
    to: seed.to || to,
    sort: usersPageSort(seed.sort),
    filters: usersPageQueryFilterSelection(seed.filters),
  };

  return filter;
};
